<template>
  <cst-page-template :hide-menu="!isLoading && changeOrderIsValid">
    <change-order-navigation-bar v-if="!isLoading && changeOrderIsValid" />
    <div class="container" :class="getProportionalHeightForFixedActionsButtonsBar()">
      <div class="change-order-page--wrapper">
        <loader v-if="isLoading" />
        <div v-if="!isLoading && changeOrderIsValid">
          <section>
            <div style="padding-top: 250px">
              <modal
                v-if="audiExclusiveNotice === 'BLOCKED' && !audiExclusiveModalDismissed"
                :title="i18n.t('/change-order/audi-exclusive/title')"
                :z-index="999"
                max-width="40%"
                @close="audiExclusiveModalDismissed = true"
              >
                <alert type="warning" :text="i18n.t('/change-order/audi-exclusive/blocked')" />
                <div class="audi-exclusive-close-button">
                  <app-button @click="audiExclusiveModalDismissed = true">
                    {{ i18n.t('/Common/Close') }}
                  </app-button>
                </div>
              </modal>
              <configuration-summary />
              <proposition-block />
              <template v-if="!hasQuoteId">
                <establishment-form />
                <customer-form />
                <reservation-form />
                <license-plate-form v-if="dealerSettings.isImporter" />
                <cancel-order-form />
                <remark-text-area />
              </template>
              <change-order-actions />
            </div>
          </section>
        </div>
      </div>
    </div>
  </cst-page-template>
</template>

<script>
import CstPageTemplate from '@/templates/CstPageTemplate.vue'
import dashboardType from '@/helpers/enums/dashboardType'
import { mapGetters } from 'vuex'
import ConfigurationSummary from '@/components/changeOrder/presentational/organisms/changeOrderConfiguration/ConfigurationSummary.vue'
import changeOrderService from '@/services/changeOrderService'
import PropositionBlock from '@/components/changeOrder/presentational/molecules/propositionBlock/PropositionBlock.vue'
import ChangeOrderActions from '@/components/changeOrder/presentational/organisms/changeOrderActions/ChangeOrderActions.vue'
import Loader from '@/components/generic/presentational/atoms/loader/Loader.vue'
import EstablishmentForm from '@/components/changeOrder/presentational/molecules/establishmentForm/EstablishmentForm.vue'
import CustomerForm from '@/components/changeOrder/presentational/molecules/customerForm/CustomerForm.vue'
import ReservationForm from '@/components/changeOrder/presentational/molecules/reservationForm/ReservationForm.vue'
import LicensePlateForm from '@/components/changeOrder/presentational/molecules/licenseplateForm/LicensePlateForm.vue'
import CancelOrderForm from '@/components/changeOrder/presentational/molecules/CancelOrderForm/CancelOrderForm.vue'
import ChangeOrderNavigationBar from '@/components/changeOrder/presentational/organisms/changeOrderNavigationBar/changeOrderNavigationBar.vue'
import RemarkTextArea from '@/components/changeOrder/presentational/organisms/RemarkTextArea/remarkTextArea.vue'
import { EventBus } from '@/plugins/event-bus'
import Modal from '@/components/generic/presentational/molecules/modal/Modal.vue'
import Alert from '@/components/generic/presentational/atoms/alert/Alert.vue'
import AppButton from '@/components/generic/presentational/atoms/appButton/AppButton.vue'

export default {
  components: {
    RemarkTextArea,
    ChangeOrderNavigationBar,
    CancelOrderForm,
    LicensePlateForm,
    ReservationForm,
    CustomerForm,
    EstablishmentForm,
    Loader,
    ChangeOrderActions,
    PropositionBlock,
    ConfigurationSummary,
    CstPageTemplate,
    Modal,
    Alert,
    AppButton,
  },
  props: {
    changeOrderId: {
      type: String,
      default: '',
    },
    komNumber: {
      type: String,
      default: '',
    },
    komYear: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      isLoading: false,
      dashboardType: dashboardType,
      i18n: window.VUE_CLI_EXPORTS.plugins.i18n,
      audiExclusiveModalDismissed: false,
    }
  },
  computed: {
    ...mapGetters({
      changeOrder: 'changeOrder/get',
      dealerSettings: 'user/dealerSettings',
    }),
    audiExclusiveNotice() {
      return this.changeOrder?.AudiExclusiveNotice
    },
    changeOrderIsValid() {
      return this.changeOrder?.CarConfiguration?.ValidStatus?.IsValid
    },
    hasQuoteId() {
      return this.changeOrder?.QuoteId
    },
  },
  watch: {
    $route(to, from) {
      const fromChangeOrderId = from?.params?.changeOrderId
      const toChangeOrderId = to?.params?.changeOrderId
      if (!fromChangeOrderId || fromChangeOrderId === toChangeOrderId) {
        return
      }

      this.$store.dispatch('changeOrder/clearChangeOrder')
      this.initPage()
    },
  },
  async mounted() {
    await this.initPage()
  },
  methods: {
    async initPage() {
      this.isLoading = true

      try {
        if (this.changeOrderId) {
          // load existing change order by ID
          await changeOrderService.getChangeOrderByChangeOrderId(this.changeOrderId)
        } else if (this.komNumber) {
          // Check if we need to fetch data:
          // - If store is empty (page refresh), always fetch
          // - If store has data but for different KomNumber, clear and fetch
          // - If store has data for same KomNumber, skip fetch (navigation from overview)
          const needsFetch =
            !this.changeOrder?.KomNumber || this.changeOrder.KomNumber !== this.komNumber

          if (needsFetch) {
            // Clear store before fetching new data
            this.$store.dispatch('changeOrder/clearChangeOrder')
            let komYear = this.komYear || this.$route.query.komYear
            let quoteId = this.$route.query.quoteId //back reference from quote
            await changeOrderService.getChangeOrderByKomNumber(this.komNumber, komYear, quoteId)
          }
        }
      } catch (error) {
        let errorData = error?.response?.data
        let komYear = this.komYear || this.$route.query.komYear

        let changeOrder = errorData?.ValidationMessages
          ? errorData
          : {
              ValidationMessages: {
                GenericResult: {
                  ErrorMessages: [this.getNotFoundErrorMessage()],
                },
              },
            }

        let genericResult = changeOrder?.ValidationMessages?.GenericResult
        EventBus.$emit('notification-warning', genericResult?.ErrorMessages.join('\n'))
      } finally {
        this.isLoading = false
      }
    },
    getNotFoundErrorMessage() {
      let komYear = this.komYear || this.$route.query.komYear
      if (komYear) {
        return `De change order met komnummer ${this.komNumber} en komjaar ${komYear} werd niet gevonden.`
      }
      return `De change order met komnummer ${this.komNumber} werd niet gevonden.`
    },
    getProportionalHeightForFixedActionsButtonsBar() {
      return this.hasQuoteId
        ? 'body-with-fixed-action-buttons--extended'
        : 'body-with-fixed-action-buttons'
    },
  },
}
</script>

<style lang="scss" scoped>
.container {
  @include center;
  display: grid;
  row-gap: 20px;
}

.body-with-fixed-action-buttons {
  padding-bottom: 70px;
}

.body-with-fixed-action-buttons--extended {
  padding-bottom: 170px;
}

.audi-exclusive-close-button {
  margin-top: 20px;
  display: flex;
  justify-content: center;
}
</style>
